import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

const rootSx = (theme) => ({
  paddingTop: '24px',
  paddingBottom: '17px',
  borderBottom: `1px solid ${theme.tokens.matterColorNegative}`,
});

const filterLabelBaseSx = (theme) => ({
  ...theme.mixins.marketplaceH3FontStyles,
  marginTop: 0,
  marginBottom: '12px',
  padding: '4px 0 2px 0',
});

const filterLabelSx = (theme) => ({
  ...filterLabelBaseSx(theme),
  color: theme.tokens.matterColorDark,
});

const filterLabelSelectedSx = (theme) => ({
  ...filterLabelBaseSx(theme),
  color: theme.tokens.marketplaceColor,
});

const labelButtonSx = {
  outline: 'none',
  textAlign: 'left',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

const optionsContainerOpenSx = {
  height: 'auto',
  paddingBottom: '30px',
};

const optionsContainerClosedSx = {
  height: 0,
  overflow: 'hidden',
};

const optionBorderBaseSx = (theme) => ({
  position: 'absolute',
  height: 'calc(100% - 12px)',
  top: '4px',
  left: '-24px',
  transition: `width ${theme.tokens.transitionStyleButton}`,
  backgroundColor: theme.tokens.matterColorDark,
});

const optionBorderSx = (theme) => ({
  ...optionBorderBaseSx(theme),
  width: 0,
});

const optionBorderSelectedSx = (theme) => ({
  ...optionBorderBaseSx(theme),
  width: '8px',
});

const optionBaseSx = (theme) => ({
  ...theme.mixins.marketplaceDefaultFontStyles,
  fontWeight: theme.tokens.fontWeightMedium,
  fontSize: '18px',
  display: 'block',
  position: 'relative',
  margin: 0,
  padding: '4px 0 8px 20px',
  outline: 'none',
  border: 'none',
  cursor: 'pointer',
  '&:focus, &:hover': {
    color: theme.tokens.matterColorDark,
  },
  '&:disabled': {
    color: theme.tokens.matterColorAnti,
    cursor: 'default',
  },
});

const optionSx = (theme) => ({
  ...optionBaseSx(theme),
  color: theme.tokens.matterColor,
});

const optionSelectedSx = (theme) => ({
  ...optionBaseSx(theme),
  color: theme.tokens.matterColorDark,
});

const optionLabel = (options, key, intl) => {
  const option = options.find((o) => o.key === key);
  return option ? intl.formatMessage({ id: option.label }) : key;
};

/**
 * @param {object} props - Component props
 * @param {string} [props.rootClassName] - Root class name override
 * @param {string} [props.className] - Additional class name
 * @param {string} props.label - Default menu label
 * @param {Array<{key: string, label: string, longLabel?: string, disabled?: boolean}>} props.options - Sort options
 * @param {string} [props.initialValue] - Currently selected option key
 * @param {string} props.urlParam - URL param name passed back to onSelect
 * @param {Function} props.onSelect - Called with (urlParam, optionKey) on selection
 * @param {boolean} props.annualPassIsValid - Whether the annual pass is valid (hides price options)
 * @returns {React.ReactNode} - Component
 */
const SortByPlain = ({
  rootClassName = null,
  className = null,
  label,
  options,
  initialValue = null,
  urlParam,
  onSelect,
  annualPassIsValid,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const intl = useIntl();

  const selectOption = (option, e) => {
    onSelect(urlParam, option);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  };

  const toggleIsOpen = () => setIsOpen((prev) => !prev);

  const menuLabel =
    initialValue && !annualPassIsValid
      ? optionLabel(options, initialValue, intl)
      : initialValue.includes('price') && annualPassIsValid
      ? label
      : initialValue && annualPassIsValid
      ? optionLabel(options, initialValue, intl)
      : label;

  const labelSx = initialValue ? filterLabelSelectedSx : filterLabelSx;

  return (
    <Box
      className={classNames(rootClassName, className)}
      sx={rootClassName ? undefined : rootSx}
    >
      <Box sx={labelSx}>
        <Box component="button" onClick={toggleIsOpen} sx={labelButtonSx}>
          <Box component="span" sx={labelSx}>
            {menuLabel}
          </Box>
        </Box>
      </Box>
      <Box sx={isOpen ? optionsContainerOpenSx : optionsContainerClosedSx}>
        {options.map((option) => {
          if (annualPassIsValid && option.key.includes('price')) return null;
          // check if this option is selected
          const selected = initialValue === option.key;
          return (
            <Box
              component="button"
              disabled={option.disabled}
              key={option.key}
              onClick={() => (selected ? null : selectOption(option.key))}
              sx={selected ? optionSelectedSx : optionSx}
            >
              <Box
                component="span"
                sx={selected ? optionBorderSelectedSx : optionBorderSx}
              />
              {intl.formatMessage({ id: option.longLabel || option.label })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SortByPlain;
